import { useAuth } from "@livcg/nodecg-react";
import {
  LayoutDashboard,
  LayoutGrid,
  LayoutList,
  MonitorPlay,
  Radio,
  Rows3,
  ScrollText,
} from "lucide-react";
import type { ReactNode } from "react";
import { BrandMark } from "../shared/BrandMark.js";
import { UserMenu } from "../shared/UserMenu.js";
import { WorkspaceSwitcher } from "../shared/WorkspaceSwitcher.js";
import { Button, SegmentedControl } from "../ui/index.js";
import { StreamLinks } from "./StreamLinks.js";

export type MatchDayView = "classic" | "control" | "deck" | "efir";

type Props = {
  activeSlug: string | null;
  activeName: string | null;
  view: MatchDayView;
  onViewChange: (view: MatchDayView) => void;
  /** Return to the project picker screen (clears only this browser's selection). */
  onLeave: () => void;
  onOpenLog: () => void;
  /** Number of errors/warnings that arrived while the log was closed. */
  unseenProblems: number;
  /** Who else is in this match. */
  presence?: ReactNode;
};

/** Topbar — branding + current project + signed-in operator badge. Switching
 *  projects goes through the full-screen ProjectPicker (the "Loyihalar" button). */
export function ProjectBar({
  activeSlug,
  activeName,
  view,
  onViewChange,
  onLeave,
  onOpenLog,
  unseenProblems,
  presence,
}: Props) {
  const { user, logout } = useAuth();

  // relative z-40: backdrop-blur creates a stacking context — without z the bar
  // ended up under positioned elements in the content, and the popovers (picker,
  // Stream) intersected with the tiles.
  return (
    <div className="relative z-40 flex items-center justify-between gap-4 px-8 py-3 border-b border-divider bg-canvas-veil backdrop-blur-md">
      <div className="flex items-center gap-3">
        <BrandMark
          icon={<Radio className="h-3.5 w-3.5" strokeWidth={2.5} />}
          title="PFL LIVCG"
          subtitle="Broadcast control"
          href="/livcg"
        />

        {activeSlug && (
          <>
            <div className="h-6 w-px bg-divider mx-2" />
            <Button
              variant="ghost"
              size="sm"
              onClick={onLeave}
              title="Loyiha tanlash ekraniga qaytish"
            >
              <LayoutList className="h-3.5 w-3.5" /> Loyihalar
            </Button>
            <span className="inline-flex items-center gap-2 text-[13px]">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-success animate-pulse" />
              <span className="font-medium">{activeName ?? activeSlug}</span>
              <span className="text-ink-muted">/{activeSlug}</span>
            </span>
          </>
        )}

        {activeSlug && <StreamLinks slug={activeSlug} />}
      </div>

      <div className="flex items-center gap-3">
        {/* Classic ⇄ Control Room view toggle */}
        <SegmentedControl<MatchDayView>
          options={[
            {
              value: "efir",
              label: (
                <>
                  <MonitorPlay className="h-3.5 w-3.5" /> Efir
                </>
              ),
              title: "Efir — preview → efirga, katta tugmalar",
            },
            {
              value: "control",
              label: (
                <>
                  <LayoutGrid className="h-3.5 w-3.5" /> Control
                </>
              ),
              title: "Control Room",
            },
            {
              value: "deck",
              label: (
                <>
                  <LayoutDashboard className="h-3.5 w-3.5" /> Deck
                </>
              ),
              title: "Deck — touch plitkalar",
            },
            {
              value: "classic",
              label: (
                <>
                  <Rows3 className="h-3.5 w-3.5" /> Klassik
                </>
              ),
              title: "Klassik tablar",
            },
          ]}
          value={view}
          onChange={onViewChange}
        />
        <Button
          variant="ghost"
          size="sm"
          onClick={onOpenLog}
          title="Operator log"
          className="relative"
        >
          <ScrollText className="h-3.5 w-3.5" /> Log
          {unseenProblems > 0 && (
            <span className="absolute -right-1 -top-1 grid h-4 min-w-4 place-items-center rounded-full bg-danger px-1 text-[10px] font-semibold text-white">
              {unseenProblems > 99 ? "99+" : unseenProblems}
            </span>
          )}
        </Button>
        {presence}
        <div className="h-6 w-px bg-divider" />
        <WorkspaceSwitcher active="match-day" user={user} />
        <UserMenu user={user} onLogout={logout} />
      </div>
    </div>
  );
}
